import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ncfAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext.jsx';
import NcfUploadModal from '../components/ncf/NcfUploadModal.jsx';
import NcfJobDivaPanel from '../components/ncf/NcfJobDivaPanel.jsx';
import NcfChangeModal from '../components/ncf/NcfChangeModal.jsx';
import NcfContractEndModal from '../components/ncf/NcfContractEndModal.jsx';
import NcfStatusFilter from '../components/ncf/NcfStatusFilter.jsx';
import { NCF_CHANGE_STATUSES } from '../utils/ncfReviewRoot.js';
import { ncfPathFromRecord } from '../utils/ncfSlug.js';
import NcfTemplateAdmin from './NcfTemplateAdmin.jsx';
import './NCF.css';

function NCFs() {
  const { user, hasPermission, hasAnyPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => hasAnyPermission(['ncf:read:own', 'ncf:read:any']) ? 'history' : 'admin');
  const [records, setRecords] = useState([]);
  const [scopeAll, setScopeAll] = useState(false);
  const [status, setStatus] = useState(null);
  const [startDateFrom, setStartDateFrom] = useState('');
  const [startDateTo, setStartDateTo] = useState('');
  const [submittedUpdatedFrom, setSubmittedUpdatedFrom] = useState('');
  const [submittedUpdatedTo, setSubmittedUpdatedTo] = useState('');
  const historyRequestRef = useRef(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [columnFilters, setColumnFilters] = useState({
    candidate: '',
    client: '',
    owner: ''
  });
  const [showChooseModal, setShowChooseModal] = useState(false);
  const [editingStartDateId, setEditingStartDateId] = useState(null);
  const [editingStartDateValue, setEditingStartDateValue] = useState('');
  const [startDateBusyId, setStartDateBusyId] = useState(null);
  const [contractEndModal, setContractEndModal] = useState({ show: false, record: null, value: '', reason: '', busy: false, error: '' });
  const [changeNcfId, setChangeNcfId] = useState(null);

  const canRead = hasAnyPermission(['ncf:read:own', 'ncf:read:any', 'ncf:approve:assigned']);
  const canWrite = hasAnyPermission(['ncf:write:own', 'ncf:write:any']);
  const canManageWorkflow = hasAnyPermission(['ncf:write:any']);
  const canAdmin = hasPermission('ncf:admin:access');
  const currentUserId = user?.userId || user?.UserId || user?.id || '';
  const canWriteRecord = (record) => canManageWorkflow
    || (hasPermission('ncf:write:own') && String(record.UserId || record.userId || '') === String(currentUserId));

  useEffect(() => {
    if (activeTab === 'history' && !canRead && canAdmin) setActiveTab('admin');
    if (activeTab === 'admin' && !canAdmin && canRead) setActiveTab('history');
  }, [activeTab, canRead, canAdmin]);

  useEffect(() => {
    if (canWrite && new URLSearchParams(location.search).get('new') === '1') {
      setShowChooseModal(true);
      navigate('/ncfs', { replace: true });
    }
  }, [canWrite, location.search, navigate]);

  const statusLabel = (code) => statuses.find(item => item.code === code)?.name || code;
  const setColumnFilter = (name, value) => setColumnFilters(prev => ({ ...prev, [name]: value }));
  const textMatches = (value, filter) => !filter || String(value || '').toLowerCase().includes(filter.toLowerCase());
  const formatDate = (value) => {
    if (!value) return '-';
    // Pure date strings (e.g. DesiredStartDate) are UTC calendar dates; parse as local
    // to avoid a one-day-back shift when the browser is behind UTC (toLocaleDateString).
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch.map(Number);
      return new Date(year, month - 1, day).toLocaleDateString();
    }
    return new Date(value).toLocaleDateString();
  };
  const toLocalDateOnly = (value) => {
    if (value == null || String(value).trim() === '') return null;
    const text = String(value).trim();
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch.map(Number);
      return new Date(year, month - 1, day);
    }
    const parsed = new Date(text);
    return Number.isNaN(parsed.getTime()) ? null : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  };
  const isDraftStartDateBeforeCreated = (startDate, createdAt, recordStatus) => {
    if (String(recordStatus || '').toLowerCase() !== 'draft') return false;
    const start = toLocalDateOnly(startDate);
    const created = toLocalDateOnly(createdAt);
    return Boolean(start && created && start < created);
  };
  // Start-date verification only makes sense once HR has cleared the consultant to start.
  const isDateReached = (value) => {
    const date = toLocalDateOnly(value);
    if (!date) return false;
    const today = toLocalDateOnly(new Date().toISOString());
    return date <= today;
  };
  const isContractEndDatePast = (value) => {
    const date = toLocalDateOnly(value);
    const today = toLocalDateOnly(new Date().toISOString());
    return Boolean(date && today && date < today);
  };
  const isWithinOneWeek = (value) => {
    const date = toLocalDateOnly(value);
    if (!date) return false;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateUtc = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    return Math.abs(dateUtc - todayUtc) <= 7 * 24 * 60 * 60 * 1000;
  };
  const isClearedForStart = (code) => ['onboarded'].includes(statusLabel(code).trim().toLowerCase());
  const filteredRecords = records.filter((record) => {
    const firstName = record.CandidateFirstName || record.candidateFirstName || '';
    const lastName = record.CandidateLastName || record.candidateLastName || '';
    const email = record.CandidateEmail || record.candidateEmail || '';
    const candidate = `${firstName} ${lastName} ${email}`;
    return textMatches(candidate, columnFilters.candidate)
      && textMatches(record.ClientName || record.clientName, columnFilters.client)
      && textMatches(record.OwnerName || record.ownerName, columnFilters.owner);
  });

  useEffect(() => {
    if (!canRead) return;
    ncfAPI.statuses().then(response => setStatuses(response.data.statuses || [])).catch(() => setStatuses([]));
  }, [canRead]);

  const loadRecords = async () => {
    const requestId = ++historyRequestRef.current;
    if (startDateFrom && startDateTo && startDateFrom > startDateTo) {
      setError('Start date From must be on or before To.');
      setLoading(false);
      return;
    }
    if (submittedUpdatedFrom && submittedUpdatedTo && submittedUpdatedFrom > submittedUpdatedTo) {
      setError('Submitted/Updated From must be on or before To.');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await ncfAPI.list({ status: status === null ? undefined : JSON.stringify(status), startDateFrom: startDateFrom || undefined, startDateTo: startDateTo || undefined, submittedUpdatedFrom: submittedUpdatedFrom || undefined, submittedUpdatedTo: submittedUpdatedTo || undefined, search: search || undefined });
      if (requestId !== historyRequestRef.current) return;
      setRecords(data.records || data.ncfs || []);
      setScopeAll(Boolean(data.scopeAll));
      setError('');
    } catch (err) {
      if (requestId !== historyRequestRef.current) return;
      setError(err?.response?.data?.error || 'Could not load NCF history.');
    } finally {
      if (requestId === historyRequestRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (canRead) loadRecords();
  }, [status, startDateFrom, startDateTo, submittedUpdatedFrom, submittedUpdatedTo, canRead]);

  const handleSearch = (event) => {
    event.preventDefault();
    loadRecords();
  };

  const handleCreated = (ncf) => {
    window.location.href = ncfPathFromRecord(ncf);
  };

  const deleteNcf = async (record) => {
    const id = record.NcfId || record.ncfId;
    const name = `${record.CandidateFirstName || record.candidateFirstName || ''} ${record.CandidateLastName || record.candidateLastName || ''}`.trim() || 'this NCF';
    if (!window.confirm(`Delete ${name}? This will remove the NCF and its documents.`)) return;

    setDeletingId(id);
    try {
      await ncfAPI.remove(id);
      await loadRecords();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not delete the NCF.');
    } finally { setDeletingId(null); }
  };

  const toDateInputValue = (value) => {
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime()) ? date.toISOString().slice(0, 10) : '';
  };

  const startEditingStartDate = (record) => {
    const id = record.NcfId || record.ncfId;
    setEditingStartDateId(id);
    setEditingStartDateValue(toDateInputValue(record.DesiredStartDate || record.desiredStartDate));
  };

  const saveStartDate = async (id) => {
    if (!editingStartDateValue) { setEditingStartDateId(null); return; }
    setStartDateBusyId(id);
    try {
      await ncfAPI.updateStartDate(id, editingStartDateValue);
      setEditingStartDateId(null);
      await loadRecords();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not update the start date.');
    } finally { setStartDateBusyId(null); }
  };

  const verifyStartDate = async (record) => {
    const id = record.NcfId || record.ncfId;
    setStartDateBusyId(id);
    try {
      await ncfAPI.verifyStartDate(id);
      await loadRecords();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not verify the start date.');
    } finally { setStartDateBusyId(null); }
  };

  // Submitting the end date notifies HR and marks the NCF as ended.
  const openContractEndModal = (record) => {
    setContractEndModal({
      show: true,
      record,
      value: toDateInputValue(record.ContractEndDate || record.contractEndDate),
      noStart: false,
      reason: '',
      busy: false,
      error: ''
    });
  };

  const closeContractEndModal = () => setContractEndModal({ show: false, record: null, value: '', reason: '', busy: false, error: '' });

  const submitContractEndDate = async () => {
    if (!contractEndModal.noStart && !contractEndModal.value) {
      setContractEndModal(previous => ({ ...previous, error: 'Enter a contract end date.' }));
      return;
    }
    if (!contractEndModal.reason.trim()) {
      setContractEndModal(previous => ({ ...previous, error: 'Enter a reason for the contract end.' }));
      return;
    }
    const id = contractEndModal.record.NcfId || contractEndModal.record.ncfId;
    setContractEndModal(previous => ({ ...previous, busy: true, error: '' }));
    try {
      await ncfAPI.updateContractEndDate(id, contractEndModal.noStart ? null : contractEndModal.value, contractEndModal.reason.trim(), Boolean(contractEndModal.noStart));
      closeContractEndModal();
      await loadRecords();
    } catch (err) {
      setContractEndModal(previous => ({ ...previous, busy: false, error: err?.response?.data?.error || 'Could not set the contract end date.' }));
    }
  };

  return (
    <main className="page-container ncf-page">
      <div className="page-header">
        <div>
          <h1>NCFs</h1>
          <p className="page-subtitle">New consultant forms and onboarding status</p>
        </div>
        {canWrite && activeTab === 'history' && (
          <button type="button" className="btn btn-primary" onClick={() => setShowChooseModal(true)}>
            + New NCF
          </button>
        )}
      </div>

      <nav className="ncf-subtabs" aria-label="NCF sections">
        {canRead && <button type="button" className={activeTab === 'history' ? 'active' : ''} onClick={() => setActiveTab('history')}><span>History</span><small>Track submissions</small></button>}
        {canWrite && <button type="button" className={activeTab === 'jobdiva' ? 'active' : ''} onClick={() => setActiveTab('jobdiva')}><span>JobDiva</span><small>Prefill from records</small></button>}
        {canWrite && <button type="button" className={activeTab === 'manual' ? 'active' : ''} onClick={() => setActiveTab('manual')}><span>Manual Upload</span><small>Start from documents</small></button>}
        {canAdmin && <button type="button" className={activeTab === 'admin' ? 'active' : ''} onClick={() => setActiveTab('admin')}><span>Admin</span><small>Manage templates</small></button>}
      </nav>

      {activeTab === 'jobdiva' && <NcfJobDivaPanel />}
      {activeTab === 'admin' && canAdmin && <NcfTemplateAdmin />}
      {activeTab === 'manual' && canWrite && (
        <NcfUploadModal onClose={() => setActiveTab('history')} onCreated={handleCreated} />
      )}
      {showChooseModal && <div className="modal-overlay" onClick={() => setShowChooseModal(false)}><div className="modal-content" onClick={(e) => e.stopPropagation()}><div className="modal-header"><h2>New Consultant Form</h2><button onClick={() => setShowChooseModal(false)} className="close-btn">✕</button></div><p>How would you like to create this NCF?</p><div className="modal-actions"><button type="button" className="btn btn-secondary" onClick={() => { setShowChooseModal(false); setActiveTab('jobdiva'); }}>Start from JobDiva</button><button type="button" className="btn btn-primary" onClick={() => { setShowChooseModal(false); setActiveTab('manual'); }}>Manual Upload</button></div></div></div>}

      {changeNcfId && <NcfChangeModal ncfId={changeNcfId} onClose={() => setChangeNcfId(null)} onSubmitted={async () => { setChangeNcfId(null); await loadRecords(); }} />}

      {contractEndModal.show && <NcfContractEndModal state={contractEndModal} setState={setContractEndModal} onClose={closeContractEndModal} onSubmit={submitContractEndDate} isPastDate={isContractEndDatePast} canNoStart={['submitted', 'approved', 'onboarded', 'completed', 'ending'].includes(contractEndModal.record?.Status || contractEndModal.record?.status)} />}

      {activeTab === 'history' && canRead && <><section className="ncf-history-panel"><div className="ncf-section-header"><div><p className="ncf-kicker">Onboarding pipeline</p><h2>Submission history</h2><p>Review candidate records, status, and generated documents.</p></div><span className="ncf-record-count">{filteredRecords.length} shown</span></div><div className="toolbar ncf-toolbar">
        <form onSubmit={handleSearch} className="ncf-search-form">
          <input
            type="search"
            value={search}
            placeholder="Search candidate, client or JobDiva ID"
            onChange={(event) => setSearch(event.target.value)}
          />
          <button type="submit" className="btn btn-secondary">Search</button>
        </form>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {scopeAll && <div className="alert alert-info">Showing NCFs from all users.</div>}

      {loading && <p role="status">Loading NCF history...</p>}
      {!loading && records.length === 0 && status === null && !startDateFrom && !startDateTo && !submittedUpdatedFrom && !submittedUpdatedTo && !search ? (
        <div className="empty-state"><h2>No NCFs yet</h2><p>Create an NCF to begin consultant onboarding.</p></div>
      ) : (
        <div className="table-container">
          <table className="data-table" aria-busy={loading}>
            <thead>
              <tr><th>Candidate</th><th>Client</th><th>Start date</th><th>Status</th><th>Owner</th><th>Submitted / Updated</th><th aria-label="Actions" /></tr>
              <tr className="ncf-column-filters">
                <th><input value={columnFilters.candidate} onChange={(event) => setColumnFilter('candidate', event.target.value)} placeholder="Filter candidate" /></th>
                <th><input value={columnFilters.client} onChange={(event) => setColumnFilter('client', event.target.value)} placeholder="Filter client" /></th>
                <th><div className="ncf-start-date-range"><label>From<input type="date" aria-label="Start date from" value={startDateFrom} max={startDateTo || undefined} onChange={event => setStartDateFrom(event.target.value)} /></label><label>To<input type="date" aria-label="Start date to" value={startDateTo} min={startDateFrom || undefined} onChange={event => setStartDateTo(event.target.value)} /></label></div></th>
                <th><NcfStatusFilter statuses={statuses} selected={status} onChange={setStatus} compact /></th>
                <th><input value={columnFilters.owner} onChange={(event) => setColumnFilter('owner', event.target.value)} placeholder="Filter owner" /></th>
                <th><div className="ncf-start-date-range"><label>From<input type="date" aria-label="Submitted or updated from" value={submittedUpdatedFrom} max={submittedUpdatedTo || undefined} onChange={event => setSubmittedUpdatedFrom(event.target.value)} /></label><label>To<input type="date" aria-label="Submitted or updated to" value={submittedUpdatedTo} min={submittedUpdatedFrom || undefined} onChange={event => setSubmittedUpdatedTo(event.target.value)} /></label></div></th>
                <th><button type="button" className="ncf-clear-filters" onClick={() => { setColumnFilters({ candidate: '', client: '', owner: '' }); setStartDateFrom(''); setStartDateTo(''); setSubmittedUpdatedFrom(''); setSubmittedUpdatedTo(''); setStatus(null); }}>Clear</button></th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length === 0 && <tr><td colSpan={7}>No records match the selected filters.</td></tr>}
              {filteredRecords.map((record) => {
                const id = record.NcfId || record.ncfId;
                const canWriteThisRecord = canWriteRecord(record);
                const firstName = record.CandidateFirstName || record.candidateFirstName || '';
                const lastName = record.CandidateLastName || record.candidateLastName || '';
                const candidateName = `${firstName} ${lastName}`.trim() || 'NCF';
                const submissionReason = record.reviewedData?.ncf?.submissionReason || '';
                const recordStatus = record.Status || record.status || 'draft';
                const updateRowStatus = async (event) => {
                  const nextStatus = event.target.value;
                  try {
                    const response = await ncfAPI.updateStatus(id, nextStatus);
                    const notification = response.data?.notification;
                    if (notification?.failedCount > 0) {
                      setError(`Status updated, but ${notification.failedCount} notification${notification.failedCount === 1 ? '' : 's'} could not be delivered.`);
                    } else {
                      setError('');
                    }
                    await loadRecords();
                  } catch (err) {
                    setError(err?.response?.data?.error || 'Could not update the NCF status.');
                  }
                };
                const availableStatusChanges = statuses.filter(item => item.isActive && item.code !== recordStatus && item.code !== 'submitted');
                const startDateVerified = Boolean(record.StartDateVerified || record.startDateVerified);
                const startDateVerifiedByName = record.StartDateVerifiedByName || record.startDateVerifiedByName;
                const startDateVerifiedAt = record.StartDateVerifiedAt || record.startDateVerifiedAt;
                const startDateBusy = startDateBusyId === id;
                const startDateValue = record.DesiredStartDate || record.desiredStartDate;
                const createdAtValue = record.CreatedAt || record.createdAt;
                const showStartDateWarning = isDraftStartDateBeforeCreated(startDateValue, createdAtValue, recordStatus);
                const contractEndDateValue = record.ContractEndDate || record.contractEndDate;
                const showContractEndDate = Boolean(contractEndDateValue) && isWithinOneWeek(contractEndDateValue);
                const contractEnded = String(recordStatus).toLowerCase() === 'ended';
                return (
                  <tr key={id} className={contractEnded ? 'ncf-row-contract-ended' : undefined}>
                    <td><Link to={ncfPathFromRecord(record)}>{`${firstName} ${lastName}`.trim() || 'Unnamed candidate'}</Link>{submissionReason && <small>Submission reason: {submissionReason}</small>}</td>
                    <td>{record.ClientName || record.clientName || '-'}</td>
                    <td className="ncf-start-date-cell">
                      {editingStartDateId === id ? (
                        <span className="ncf-start-date-edit">
                          <input
                            type="date"
                            value={editingStartDateValue}
                            onChange={(event) => setEditingStartDateValue(event.target.value)}
                            disabled={startDateBusy}
                            aria-label={`Start date for ${candidateName}`}
                          />
                          {showStartDateWarning && <span className="ncf-start-date-warning-icon" title="Start date is before this NCF was created" aria-label="Start date is before this NCF was created">⚠</span>}
                          <button type="button" className="btn btn-secondary" onClick={() => saveStartDate(id)} disabled={startDateBusy}>Save</button>
                          <button type="button" className="btn btn-link" onClick={() => setEditingStartDateId(null)} disabled={startDateBusy}>Cancel</button>
                        </span>
                      ) : (
                        <span className="ncf-start-date-display">
                          {canWriteThisRecord ? (
                            <button type="button" className="ncf-start-date-value" onClick={() => startEditingStartDate(record)} title="Click to change the start date">
                              {formatDate(startDateValue)}
                            </button>
                          ) : formatDate(startDateValue)}
                          {showStartDateWarning && <span className="ncf-start-date-warning-icon" title="Start date is before this NCF was created" aria-label="Start date is before this NCF was created">⚠</span>}
                          {startDateVerified ? (
                            <span className="ncf-start-date-verified" title={`Verified${startDateVerifiedByName ? ` by ${startDateVerifiedByName}` : ''}${startDateVerifiedAt ? ` on ${formatDate(startDateVerifiedAt)}` : ''}`}>✓</span>
                          ) : (canWriteThisRecord && isClearedForStart(recordStatus) && isDateReached(startDateValue)) ? (
                            <button type="button" className="ncf-verify-btn" onClick={() => verifyStartDate(record)} disabled={startDateBusy}>
                              Verify start date
                            </button>
                          ) : null}
                        </span>
                      )}
                      {showContractEndDate && (
                        <span className="ncf-end-date-display">
                          Ends {formatDate(contractEndDateValue)}
                        </span>
                      )}
                    </td>
                    <td>{canManageWorkflow ? <select className="ncf-row-status" value={recordStatus} onChange={updateRowStatus} aria-label={`Update status for ${candidateName}`}><option value={recordStatus}>{statusLabel(recordStatus)}</option>{availableStatusChanges.map(item => <option key={item.code} value={item.code}>{item.name}</option>)}</select> : <span className={`status-badge status-${recordStatus}`}>{statusLabel(recordStatus)}</span>}</td>
                    <td>{record.OwnerName || record.ownerName || '-'}</td>
                    <td><span className="ncf-date-stack"><strong>{formatDate(record.CreatedAt || record.createdAt)}</strong><small>Updated {formatDate(record.UpdatedAt || record.updatedAt)}</small></span></td>
                    <td>
                      {canWriteThisRecord && NCF_CHANGE_STATUSES.includes(recordStatus) && <button type="button" className="ncf-icon-end-date ncf-icon-change" title="Change NCF" aria-label={`Change NCF for ${candidateName}`} onClick={() => setChangeNcfId(id)}>CHANGE</button>}
                      {canWriteThisRecord && !['draft', 'nostart', 'ending', 'ended'].includes(String(recordStatus).toLowerCase().replace(/[\s_-]/g, '')) && statusLabel(recordStatus).trim().toLowerCase() !== 'no start' && <button type="button" className="ncf-icon-end-date" title="End Engagement" aria-label={`End engagement for ${candidateName}`} onClick={() => openContractEndModal(record)}>END</button>}
                      {canWriteThisRecord && (canManageWorkflow || recordStatus === 'draft') && <button type="button" className="ncf-icon-delete" title="Delete NCF" aria-label={`Delete ${candidateName}`} onClick={() => deleteNcf(record)} disabled={deletingId === id}>×</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}</section></>}

    </main>
  );
}

export default NCFs;
